/**
 * Foundation · Durate — campo di inserimento durata (unico per tutta la Suite).
 * value: ore centesimali (number|null). onCommit(num|null) al blur/Invio con il valore
 * già normalizzato secondo il formato dell'azienda. Escape ripristina.
 */
import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { parseDuration, formatDuration, sanitizeDurationTyping, durationPlaceholder } from "./durationCore";
import { useDurationFormat } from "./DurationFormatContext";

const BASE = "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-mono";

const show = (v, fmt) => (v === null || v === undefined || v === "" ? "" : formatDuration(v, fmt));

export function DurationInput({
  value, onCommit, onInvalid, allowNegative = false, className, placeholder, id,
  disabled, readOnly, "data-testid": testid, "aria-label": ariaLabel, unstyled = false,
}) {
  const fmt = useDurationFormat();
  const [draft, setDraft] = useState(() => show(value, fmt));
  const [error, setError] = useState(null);

  useEffect(() => { setDraft(show(value, fmt)); setError(null); }, [value, fmt]);

  const commit = () => {
    const { value: num, error: err } = parseDuration(draft, fmt, { allowNegative });
    if (err) { setError(err); onInvalid?.(draft, err); return; }
    setDraft(show(num, fmt));
    if (num !== (value === "" || value === undefined ? null : value)) onCommit?.(num);
  };

  return (
    <input
      id={id} type="text" inputMode={fmt === "hm" ? "text" : "decimal"} autoComplete="off"
      value={draft} placeholder={placeholder ?? durationPlaceholder(fmt)}
      disabled={disabled} readOnly={readOnly}
      onChange={(e) => { setDraft(sanitizeDurationTyping(e.target.value, fmt, { allowNegative })); setError(null); }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        else if (e.key === "Escape") { setDraft(show(value, fmt)); setError(null); e.currentTarget.blur(); }
      }}
      className={cn(unstyled ? "" : BASE, className, error && "!border-suite-error !ring-suite-error/40")}
      aria-label={ariaLabel} aria-invalid={error ? true : undefined}
      title={error || undefined} data-testid={testid} data-duration-format={fmt}
    />
  );
}

export default DurationInput;
